'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';

/**
 * App / agent store-page description with graceful truncation. Long
 * descriptions (the vector editor's runs to a paragraph) were clipped
 * mid-word by the surrounding scroll container with no affordance to
 * read the rest. This clamps to a few lines — so the clamp shows an
 * ellipsis instead of a hard cut — and reveals a "See more" toggle only
 * when the text actually overflows the clamp.
 */
export function AppDescription({ text }: { text: string }) {
  const t = useT();
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Measure against the COLLAPSED clamp on mount / text change. We only
  // check while collapsed so `scrollHeight > clientHeight` reflects the
  // clamped box; the flag then persists so the toggle stays visible once
  // the user expands.
  useLayoutEffect(() => {
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className="mt-4">
      <p
        ref={ref}
        className={cn(
          'text-sm leading-relaxed text-muted-foreground',
          !expanded && 'line-clamp-4',
        )}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-sm font-medium text-foreground transition-colors hover:underline"
          aria-expanded={expanded}
        >
          {expanded ? t('appDesc.seeLess') : t('discoverView.seeMore')}
        </button>
      )}
    </div>
  );
}
